import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { inventoryApi } from '@/lib/inventoryApi';
import { toNumber } from '@/lib/number';
import { showSuccess, showError } from '@/lib/apiErrors';
import { Dialog } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Search, Upload, Check, Package } from 'lucide-react';
import { INVENTORY_CATEGORIES } from '@/types/inventory';
import type { InventoryTemplate, ImportTemplatesRequest } from '@/types/inventory';
import type { ApiListResponse } from '@/types/api';

interface ImportTemplatesDialogProps {
  open: boolean;
  onClose: () => void;
  farmId: string;
  onSuccess?: () => void;
}

interface TemplateOverride {
  reorder_point?: number;
  unit_cost?: number;
}

export function ImportTemplatesDialog({
  open,
  onClose,
  farmId,
  onSuccess,
}: ImportTemplatesDialogProps) {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [overrides, setOverrides] = useState<Record<string, TemplateOverride>>({});

  // Fetch templates
  const { data, isLoading } = useQuery<ApiListResponse<InventoryTemplate>>({
    queryKey: ['inventory', 'templates', categoryFilter !== 'all' ? categoryFilter : undefined],
    queryFn: () =>
      inventoryApi.listTemplates(
        categoryFilter !== 'all' ? { category: categoryFilter } : undefined
      ),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  const filteredTemplates = useMemo(() => {
    const list = data?.data ?? [];
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(
      (t) =>
        t.name.toLowerCase().includes(q) || t.category.toLowerCase().includes(q)
    );
  }, [data, search]);

  // Toggle selection
  function toggleTemplate(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  // Select/deselect all visible
  function toggleAll() {
    if (filteredTemplates.every((t) => selectedIds.has(t.id))) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredTemplates.forEach((t) => next.delete(t.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredTemplates.forEach((t) => next.add(t.id));
        return next;
      });
    }
  }

  // Update override for a template
  function setOverride(templateId: string, field: keyof TemplateOverride, value: string) {
    setOverrides((prev) => ({
      ...prev,
      [templateId]: {
        ...prev[templateId],
        [field]: value ? Number(value) : undefined,
      },
    }));
  }

  // Import mutation
  const mutation = useMutation({
    mutationFn: (data: ImportTemplatesRequest) =>
      inventoryApi.importTemplates(farmId, data),
    onSuccess: (response) => {
      const result = response.data;
      const importedCount = result.imported.length;
      const skippedCount = result.skipped.length;
      if (importedCount > 0) {
        showSuccess(
          `Imported ${importedCount} item${importedCount !== 1 ? 's' : ''}` +
            (skippedCount > 0 ? ` (${skippedCount} skipped)` : '')
        );
      } else {
        showError(
          `All ${skippedCount} template${skippedCount !== 1 ? 's' : ''} were skipped (likely already imported)`
        );
      }
      onSuccess?.();
      handleClose();
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Failed to import templates');
    },
  });

  function handleImport() {
    if (selectedIds.size === 0) return;

    // Build overrides (only include entries that have values)
    const cleanOverrides: Record<string, { reorder_point?: number; unit_cost?: number }> = {};
    for (const [id, override] of Object.entries(overrides)) {
      if (selectedIds.has(id) && (override.reorder_point != null || override.unit_cost != null)) {
        cleanOverrides[id] = override;
      }
    }

    mutation.mutate({
      template_ids: Array.from(selectedIds),
      ...(Object.keys(cleanOverrides).length > 0 && { overrides: cleanOverrides }),
    });
  }

  function handleClose() {
    setSearch('');
    setCategoryFilter('all');
    setSelectedIds(new Set());
    setOverrides({});
    onClose();
  }

  const allVisibleSelected =
    filteredTemplates.length > 0 && filteredTemplates.every((t) => selectedIds.has(t.id));

  return (
    <Dialog open={open} onClose={handleClose} title="Import from Templates" size="xl">
      <div className="space-y-4">
        {/* Description */}
        <p className="text-sm text-gray-500">
          Select templates from the catalog to add as inventory items. You can customize
          reorder points and unit costs after selection.
        </p>

        {/* Search + filter */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            options={[
              { value: 'all', label: 'All Categories' },
              ...INVENTORY_CATEGORIES.map((c) => ({ value: c.value, label: c.label })),
            ]}
          />
        </div>

        {/* Template list */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size={24} className="text-primary-600" />
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Package className="h-10 w-10 text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">
              {search ? 'No templates match your search.' : 'No templates available.'}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {/* Select all */}
            <div className="flex items-center justify-between py-2 px-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAll}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Select all ({filteredTemplates.length})
                </span>
              </label>
              <span className="text-sm text-gray-400">
                {selectedIds.size} selected
              </span>
            </div>

            {/* Scrollable list */}
            <div className="max-h-[300px] overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {filteredTemplates.map((template) => (
                <TemplateRow
                  key={template.id}
                  template={template}
                  selected={selectedIds.has(template.id)}
                  onToggle={() => toggleTemplate(template.id)}
                  override={overrides[template.id]}
                  onOverrideChange={(field, value) =>
                    setOverride(template.id, field, value)
                  }
                />
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={selectedIds.size === 0}
            leftIcon={<Upload className="h-4 w-4" />}
            onClick={handleImport}
          >
            Import {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ============================================================================
// Template row
// ============================================================================

interface TemplateRowProps {
  template: InventoryTemplate;
  selected: boolean;
  onToggle: () => void;
  override?: TemplateOverride;
  onOverrideChange: (field: keyof TemplateOverride, value: string) => void;
}

function TemplateRow({
  template,
  selected,
  onToggle,
  override,
  onOverrideChange,
}: TemplateRowProps) {
  const defaultReorder = toNumber(template.default_reorder_point);
  const suggestedCost = template.suggested_unit_cost != null
    ? toNumber(template.suggested_unit_cost)
    : null;

  return (
    <div className="px-3 py-2.5 hover:bg-gray-50/50 transition-colors">
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 text-sm">{template.name}</span>
            <Badge variant="neutral" size="sm">{template.category}</Badge>
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {template.unit} | Reorder: {defaultReorder}
            {suggestedCost != null && ` | Cost: P${suggestedCost}`}
          </div>
        </div>
        {selected && <Check className="h-4 w-4 text-primary-600 shrink-0" />}
      </div>

      {/* Override fields (shown when selected) */}
      {selected && (
        <div className="flex items-center gap-3 mt-2 ml-7">
          <div className="flex-1">
            <input
              type="number"
              min="0"
              step="any"
              placeholder={`Reorder: ${defaultReorder}`}
              value={override?.reorder_point ?? ''}
              onChange={(e) => onOverrideChange('reorder_point', e.target.value)}
              className="block w-full rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 placeholder:text-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </div>
          <div className="flex-1">
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder={suggestedCost != null ? `Cost: P${suggestedCost}` : 'Unit cost'}
              value={override?.unit_cost ?? ''}
              onChange={(e) => onOverrideChange('unit_cost', e.target.value)}
              className="block w-full rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 placeholder:text-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </div>
        </div>
      )}
    </div>
  );
}
